import { initialColors } from './colors';
import { htmlDecode, nFormatter } from './util';

const Charts = {
  init(colors = initialColors) {
    window.google.charts.load('current').then(() => {
      this.drawCharts(colors);
    });
  },
  drawCharts(colors = initialColors) {
    const queryOptions = {
      sendMethod: 'auto',
    };

    const containers = document.querySelectorAll('.js-chart');
    containers.forEach((chartContainer) => {
      const dataFileUrl = chartContainer.getAttribute('data-source');
      const chartType = chartContainer.getAttribute('data-type');
      const query = new window.google.visualization.Query(
        dataFileUrl,
        queryOptions,
      );
      query.send((response) => {
        if (response.isError()) {
          console.error(
            `Error in query: ${response.getMessage()} ${response.getDetailedMessage()}`,
          );
          return;
        }

        const data = response.getDataTable();

        drawChart(chartContainer, chartType, data);
      });
    });

    const oecContainers = document.querySelectorAll('.js-oec-chart');
    oecContainers.forEach((chartContainer) => {
      const dataFileUrl = chartContainer.getAttribute('data-source');
      const chartType = chartContainer.getAttribute('data-type');
      const oecChartType = chartContainer.getAttribute('data-oec-type');
      const url = htmlDecode(dataFileUrl);

      fetch(url, { credentials: 'include' })
        .then((response) => {
          if (!response.ok) {
            return Promise.reject('Error status: ' + response.status);
          }
          return response.json();
        })
        .then((data) => {
          if (data.data && data.data.length) {
            const dataTable = new window.google.visualization.DataTable();

            if (oecChartType == 'rca_index') {
              const _data = data.data
                .sort((a, b) => b['Trade Value RCA'] - a['Trade Value RCA'])
                .slice(0, 10)
                .map((_data) => [
                  _data['HS4'],
                  {
                    v: _data['Trade Value RCA'],
                    f: Math.trunc(_data['Trade Value RCA']).toString(),
                  },
                ]);

              dataTable.addColumn('string', 'Most specialised products');
              dataTable.addColumn({
                type: 'number',
                label: 'Trade Value RCA',
              });

              dataTable.addRows(_data);

              drawChart(chartContainer, chartType, dataTable);
            } else if (
              oecChartType == 'service_imports' ||
              oecChartType == 'service_exports'
            ) {
              const productCategories = {};
              let serviceLabelType = '';

              if (oecChartType == 'service_imports')
                serviceLabelType = 'Imports';
              else if (oecChartType == 'service_exports')
                serviceLabelType = 'Exports';

              data.data
                .sort(
                  (a, b) =>
                    parseFloat(a['Service ID']) - parseFloat(b['Service ID']),
                )
                .forEach((item) => {
                  let IDs = item['Service ID'].split('.');
                  if (IDs.length < 3) {
                    let parentID = IDs[0];
                    if (
                      productCategories[parentID] &&
                      Array.isArray(productCategories[parentID])
                    )
                      productCategories[parentID].push(item);
                    else productCategories[parentID] = [item];
                  }
                });

              const normalizedData = [];
              const IDs = Object.keys(productCategories);

              IDs.forEach((id) => {
                if (productCategories[id].length > 1)
                  productCategories[id] = productCategories[id].slice(1);
                productCategories[id].forEach((item) =>
                  normalizedData.push(item),
                );
              });

              let rows = normalizedData
                .map((_data) => [_data['Service'], _data['Service Value']])
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10);

              dataTable.addColumn('string', 'Service');
              dataTable.addColumn({
                type: 'number',
                label: 'Service ' + serviceLabelType + ' (in $)',
                pattern: '#,##0',
              });
              dataTable.addRows(rows);

              chartContainer.setAttribute('data-slanted-text', 'true');

              drawChart(chartContainer, chartType, dataTable);
            } else if (
              oecChartType == 'latest_trade_imports' ||
              oecChartType == 'latest_trade_exports'
            ) {
              const years = [];
              let countries = {};

              for (let i = 0; i < data.data.length; i++) {
                const _currentData = data.data[i];
                const _year = _currentData['Year'];
                const _countryID = _currentData['Country ID'];
                const _countryName = _currentData['Country'];
                const _tradeValue = _currentData['Trade Value'];

                if (years.indexOf(_year) < 0) years.push(_year);

                if (
                  !Object.prototype.hasOwnProperty.call(countries, _countryID)
                ) {
                  countries[_countryID] = {
                    name: _countryName,
                    total: _tradeValue,
                  };
                } else {
                  countries[_countryID]['total'] += _tradeValue || 0;
                }
                countries[_countryID][_year] = _tradeValue || 0;
              }

              //Chart title
              let chartTitle = '';
              if (oecChartType == 'latest_trade_imports') {
                chartTitle = 'Imports Value ($USD)';
              } else if (oecChartType == 'latest_trade_exports') {
                chartTitle = 'Exports Value ($USD)';
              }

              //Filter top 5 countries
              const _entries = Object.entries(countries);
              _entries.sort((a, b) => b[1].total - a[1].total);
              countries = Object.fromEntries(_entries.slice(0, 5));

              //Add data to DataTable
              dataTable.addColumn({
                type: 'date',
                label: 'Year',
              });

              const dataRows = [];
              for (let i = 0; i < years.length; i++) {
                const _row = [];
                _row.push(new Date(years[i], 0));

                for (let key in countries) {
                  if (Object.prototype.hasOwnProperty.call(countries, key)) {
                    if (i == 0) {
                      dataTable.addColumn({
                        type: 'number',
                        label: countries[key].name,
                      });
                      dataTable.addColumn({
                        type: 'string',
                        role: 'annotation',
                      });
                    }

                    _row.push(countries[key][years[i]] || 0);

                    if (i == years.length - 1) {
                      _row.push(countries[key].name);
                    } else {
                      _row.push(null);
                    }
                  }
                }
                dataRows.push(_row);
              }
              dataTable.addRows(dataRows);

              drawLatestTradeLineChart(
                chartContainer,
                dataTable,
                chartTitle,
                years,
              );
            } else if (oecChartType == 'exports_over_time') {
              const meta = data.meta || {};
              const currencyCode = meta.currency || 'USD';
              const currencySymbol = meta.currency_symbol || '$';
              const isQuarterly = meta.granularity === 'quarterly';

              const sortedData = data.data.slice().sort((a, b) =>
                isQuarterly ? a['Time'] - b['Time'] : a['Year'] - b['Year']
              );

              dataTable.addColumn('string', isQuarterly ? 'Period' : 'Year');
              dataTable.addColumn({ type: 'number', label: 'Trade Value' });
              dataTable.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });

              sortedData.forEach((row, i) => {
                const value = row['Trade Value'] || 0;
                const year = row['Year'];
                const periodLabel = isQuarterly
                  ? (row['Period'] || 'Q' + row['Quarter'] + ' ' + year)
                  : String(year);

                let growthText = '';
                if (i > 0) {
                  const prev = sortedData[i - 1]['Trade Value'] || 0;
                  if (prev > 0) {
                    const growth = ((value - prev) / prev) * 100;
                    growthText = '<br>Growth: ' + growth.toFixed(1) + '%';
                  }
                }
                const tooltip =
                  '<div style="padding:8px"><b>' + periodLabel + '</b><br>' +
                  'Exports: ' + nFormatter(value, 2, currencySymbol) +
                  growthText + '</div>';

                // Axis label: yearly = first + every 5 years + last; quarterly = Q1 every 2 years + last
                let label = '';
                if (isQuarterly) {
                  if ((row['Quarter'] === 1 && year % 2 === 0) || i === sortedData.length - 1) {
                    label = String(year);
                  }
                } else {
                  const isKeyYear =
                    i === 0 || (year % 5 === 0) || i === sortedData.length - 1;
                  label = isKeyYear ? String(year) : '';
                }
                dataTable.addRow([label, value, tooltip]);
              });

              chartContainer.setAttribute('data-hide', '');

              const eotWrapper = chartContainer.querySelector('.chart__wrapper');
              const eotTextStyle = {
                color: colors.dark_5,
                fontName: '"ARSMaquettePro", Helvetica, Arial, sans-serif',
              };
              const eotChart = new window.google.visualization.ChartWrapper({
                chartType: 'LineChart',
                dataTable,
                options: {
                  backgroundColor: colors.light_7,
                  height: 400,
                  chartArea: {
                    top: 32,
                    // Marker rect: used only to locate the true plot bounds, then stripped
                    // of its top/right strokes after render so we keep an L-shape.
                    backgroundColor: { stroke: '#999', strokeWidth: 2, fill: 'transparent' },
                  },
                  legend: { position: 'none' },
                  pointsVisible: true,
                  lineWidth: 3,
                  tooltip: { isHtml: true },
                  hAxis: {
                    textStyle: eotTextStyle,
                    baselineColor: 'transparent',
                    gridlines: { color: 'transparent' },
                    showTextEvery: 1,
                    slantedText: true,
                    slantedTextAngle: 45,
                  },
                  vAxis: {
                    title: 'Exports Value (' + currencyCode + ')',
                    titleTextStyle: eotTextStyle,
                    textStyle: eotTextStyle,
                    baselineColor: 'transparent',
                    minorGridlines: { count: 0 },
                    gridlines: { color: colors.dark_5, minSpacing: 40 },
                    format: 'short',
                  },
                  colors: ['#2664ae'],
                },
              });

              // Replace the 4-sided chartArea border rect with an L-shaped polyline.
              window.google.visualization.events.addListener(eotChart, 'ready', () => {
                const svg = eotWrapper.querySelector('svg');
                if (!svg) return;

                svg.querySelectorAll('.js-custom-axes').forEach((n) => n.remove());

                // Find the largest stroked <rect>: that's the chartArea border we injected.
                let targetRect = null;
                let maxArea = 0;
                svg.querySelectorAll('rect').forEach((r) => {
                  const stroke =
                    r.getAttribute('stroke') ||
                    (r.style && r.style.stroke) ||
                    '';
                  if (!stroke || stroke.toLowerCase() === 'none') return;
                  const w = parseFloat(r.getAttribute('width') || '0');
                  const h = parseFloat(r.getAttribute('height') || '0');
                  if (w < 100 || h < 100) return;
                  const area = w * h;
                  if (area > maxArea) {
                    maxArea = area;
                    targetRect = r;
                  }
                });

                if (!targetRect) return;

                const x = parseFloat(targetRect.getAttribute('x'));
                const y = parseFloat(targetRect.getAttribute('y'));
                const w = parseFloat(targetRect.getAttribute('width'));
                const h = parseFloat(targetRect.getAttribute('height'));
                targetRect.parentNode.removeChild(targetRect);

                const ns = 'http://www.w3.org/2000/svg';
                const poly = document.createElementNS(ns, 'polyline');
                poly.setAttribute(
                  'points',
                  x + ',' + y + ' ' + x + ',' + (y + h) + ' ' + (x + w) + ',' + (y + h),
                );
                poly.setAttribute('stroke', '#999');
                poly.setAttribute('stroke-width', '2');
                poly.setAttribute('fill', 'none');
                poly.setAttribute('class', 'js-custom-axes');
                svg.appendChild(poly);
              });

              eotChart.draw(eotWrapper);
            } else if (oecChartType == 'growth_potential') {
              const _data = data.data
                .sort((a, b) => b['Difference'] - a['Difference'])
                .slice(0, 10);

              dataTable.addColumn('string', 'Product');
              dataTable.addColumn({ type: 'number', label: 'Current Exports' });
              dataTable.addColumn({ type: 'number', label: 'Growth Potential' });

              const truncate = (s, n = 32) =>
                s && s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;

              _data.forEach((row) => {
                const hsKey = Object.keys(row).find(k => k.match(/^HS\d Official$/) && !k.includes('ID'));
                const rawLabel = hsKey ? row[hsKey] : 'Unknown';
                dataTable.addRow([
                  truncate(rawLabel),
                  row['Trade Value'] || 0,
                  row['Difference'] || 0,
                ]);
              });

              chartContainer.setAttribute('data-hide', '');
              chartContainer.setAttribute('data-stacked', 'true');

              const gpWrapper = chartContainer.querySelector('.chart__wrapper');
              const gpTextStyle = {
                color: colors.dark_5,
                fontName: '"ARSMaquettePro", Helvetica, Arial, sans-serif',
              };
              const gpChart = new window.google.visualization.ChartWrapper({
                chartType: 'BarChart',
                dataTable,
                options: {
                  backgroundColor: colors.light_7,
                  height: 560,
                  chartArea: { top: 32, bottom: 80, left: 220, right: 40 },
                  isStacked: true,
                  legend: { position: 'bottom' },
                  tooltip: {
                    textStyle: { color: colors.dark_5 },
                    showColorCode: true,
                  },
                  hAxis: {
                    title: 'Exports Value ($USD)',
                    titleTextStyle: gpTextStyle,
                    textStyle: gpTextStyle,
                    format: 'short',
                    minorGridlines: { count: 0 },
                    gridlines: { color: colors.dark_5, minSpacing: 40 },
                  },
                  vAxis: {
                    textStyle: gpTextStyle,
                    baselineColor: '#fff',
                    gridlines: { color: 'transparent' },
                  },
                  bar: { groupWidth: '60%' },
                  colors: ['#2664ae', '#00be9c'],
                },
              });
              gpChart.draw(gpWrapper);
            } else {
              return Promise.reject(
                'Error: no OEC chart matches to "' + oecChartType + '"',
              );
            }
          }
        })
        .catch((e) => {
          console.error(e);
          const wrapper = chartContainer.querySelector('.chart__wrapper');
          if (wrapper)
            wrapper.innerHTML = '<p class="chart__error">No data available</p>';
        });
    });

    function drawChart(chartContainer, chartType, dataTable) {
      const hideAxis = chartContainer.getAttribute('data-hide').split(', ');
      const isStacked = chartContainer.getAttribute('data-stacked') === 'true';
      const isSlantedText = chartContainer.getAttribute('data-slanted-text');
      const customColors = chartContainer
        .getAttribute('data-custom-colors')
        .split(',')
        .filter(Boolean);

      const textChartStyle = {
        color: colors.dark_5,
        fontName: '"ARSMaquettePro", Helvetica, Arial, sans-serif',
      };

      const getHorizontalAxisTitle = () =>
        !hideAxis.find((a) => a === 'horizontal')
          ? dataTable.getColumnLabel(1)
          : '';
      const getVerticalAxisTitle = () =>
        !hideAxis.find((a) => a === 'vertical')
          ? dataTable.getColumnLabel(0)
          : '';

      const sharedOptions = {
        backgroundColor: colors.light_7,
        height: 400,
        chartArea: { top: 32 },
      };

      const sharedColors = [
        ...customColors,
        colors.dark_5,
        colors.light_1,
        colors.dark_4,
        colors.light_2,
        colors.dark_3,
        colors.light_3,
      ];

      const options = {
        LineChart: {
          ...sharedOptions,
          legend: { position: 'none' },
          pointsVisible: true,
          lineWidth: 3,
          tooltip: {
            textStyle: { color: colors.dark_5 },
          },
          hAxis: {
            title: getHorizontalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            direction: -1,
            slantedText: true,
            slantedTextAngle: 90,
            baselineColor: '#fff',
            gridlines: {
              color: 'transparent',
              minSpacing: 40,
            },
          },
          vAxis: {
            title: getVerticalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            baselineColor: '#fff',
            minorGridlines: { count: 0 },
            gridlines: {
              color: colors.dark_5,
              minSpacing: 40,
            },
          },
          colors: sharedColors,
        },
        PieChart: {
          ...sharedOptions,
          legend: { position: 'none' },
          tooltip: {
            textStyle: { color: colors.dark_5 },
            showColorCode: true,
          },
          hAxis: {
            title: getHorizontalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            gridlines: {
              color: colors.dark_5,
              minSpacing: 40,
            },
          },
          vAxis: {
            title: getVerticalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            gridlines: {
              color: colors.dark_5,
              minSpacing: 40,
            },
          },
          colors: sharedColors,
        },
        ColumnChart: {
          ...sharedOptions,
          isStacked,
          legend: {
            position: 'bottom',
            pagingTextStyle: {
              color: colors.dark_4,
            },
            scrollArrows: {
              inactiveColor: colors.dark_4,
              activeColor: colors.dark_4,
            },
          },
          hAxis: {
            title: getHorizontalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            baselineColor: '#fff',
            gridlines: {
              color: 'transparent',
              minSpacing: 40,
            },
          },
          vAxis: {
            title: getVerticalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            minorGridlines: { count: 0 },
            gridlines: {
              color: colors.dark_5,
              minSpacing: 40,
            },
          },
          bar: { groupWidth: isStacked ? '20' : '50%' },
          colors: sharedColors,
        },
        BarChart: {
          ...sharedOptions,
          legend: { position: 'none' },
          tooltip: {
            textStyle: { color: colors.dark_5 },
            showColorCode: true,
          },
          hAxis: {
            title: getHorizontalAxisTitle(),
            format: dataTable.getColumnPattern(1),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            minorGridlines: { count: 0 },
            gridlines: {
              color: colors.dark_5,
              minSpacing: 40,
            },
            ...(isSlantedText ? { slantedText: true } : ''),
          },
          vAxis: {
            title: getVerticalAxisTitle(),
            titleTextStyle: textChartStyle,
            textStyle: textChartStyle,
            baselineColor: '#fff',
            gridlines: {
              color: 'transparent',
              minSpacing: 40,
            },
          },
          bar: { groupWidth: '50%' },
          colors: sharedColors,
        },
      };

      const chart = new window.google.visualization.ChartWrapper({
        chartType,
        dataTable,
        options: options[chartType],
      });

      chart.draw(chartContainer.querySelector('.chart__wrapper'));
    }

    function drawLatestTradeLineChart(
      chartContainer,
      dataTable,
      title,
      years = [],
    ) {
      //Format table columns
      const dateFormatter = new window.google.visualization.DateFormat({
        pattern: 'yyyy',
      });

      const priceFormatter = new window.google.visualization.NumberFormat({
        prefix: '$',
        negativeParens: true,
        pattern: 'short',
      });

      const dataTableColumns = dataTable.getNumberOfColumns();

      if (dataTableColumns) {
        for (let i = 0; i < dataTableColumns; i++) {
          switch (dataTable.getColumnType(i)) {
            case 'date':
              dateFormatter.format(dataTable, i);
              break;
            case 'number':
              priceFormatter.format(dataTable, i);
              break;
            default:
              break;
          }
        }
      }

      //Chart options
      const textChartStyle = {
        color: colors.dark_5,
        fontName: '"ARSMaquettePro", Helvetica, Arial, sans-serif',
      };

      const sharedOptions = {
        backgroundColor: colors.light_7,
        height: 400,
        chartArea: { top: 32 },
      };

      const options = {
        ...sharedOptions,
        legend: { position: 'none' },
        pointsVisible: true,
        lineWidth: 3,
        tooltip: {
          textStyle: { color: colors.dark_5 },
          showColorCode: true,
        },
        annotations: {
          stem: {
            length: 0,
          },
          textStyle: {
            fontSize: 11,
          },
        },
        hAxis: {
          title: '',
          titleTextStyle: textChartStyle,
          textStyle: textChartStyle,
          baselineColor: '#fff',
          gridlines: {
            color: 'transparent',
            minSpacing: 40,
          },
          format: 'YYYY',
          ticks: years.map((_y) => new Date(_y, 0)),
        },
        vAxis: {
          title: title ? title : 'prova',
          titleTextStyle: textChartStyle,
          textStyle: textChartStyle,
          baselineColor: '#fff',
          minorGridlines: { count: 0 },
          gridlines: {
            color: colors.dark_5,
            minSpacing: 30,
          },
          format: 'short',
        },
        colors: ['#2664ae', '#d2583a', '#e8bd54', '#2f504a', '#aa82b9'],
      };

      //Create the chart
      const chart = new window.google.visualization.ChartWrapper({
        chartType: 'LineChart',
        dataTable,
        options: options,
      });

      //Add $ currency to the vAxis
      window.google.visualization.events.addListener(
        chart,
        'ready',
        function () {
          const axisLabels = chartContainer.getElementsByTagName('text');
          for (var i = 0; i < axisLabels.length; i++) {
            if (axisLabels[i].getAttribute('text-anchor') === 'end') {
              axisLabels[i].innerHTML = '$' + axisLabels[i].innerHTML;
            }
          }
        },
      );

      //Draw chart
      chart.draw(chartContainer.querySelector('.chart__wrapper'));
    }
  },
};

export default Charts;
